import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { Area } from 'react-easy-crop';
import { X, ZoomIn, Check } from 'lucide-react';
import { getCroppedImageBlob } from '../../utils/imageCropUtils';

interface ImageCropperModalProps {
  imageSrc: string | null;
  aspect?: number;
  outputSize?: number;
  cropShape?: 'rect' | 'round';
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  imageSrc,
  aspect = 1,
  outputSize = 512,
  cropShape = 'rect',
  onCancel,
  onConfirm
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  if (!imageSrc) return null;

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, outputSize);
      onConfirm(blob);
    } catch (error) {
      console.error('Erro ao recortar imagem:', error);
      alert('Não foi possível recortar a imagem. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[70] p-4 animate-[fadeIn_0.1s_ease-out_forwards]">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden max-w-lg w-full animate-[zoomIn95_0.15s_ease-out_forwards]">
        <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">Ajustar Imagem</h2>
          <button
            onClick={onCancel}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative w-full h-80 bg-slate-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-teal-600"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={processing || !croppedAreaPixels}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all font-medium shadow-lg disabled:opacity-60"
            >
              <Check className="w-4 h-4" />
              {processing ? 'Aplicando...' : 'Usar esta imagem'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
